/**
 * RemoteHome — the paired iPad home (not in a meeting). Mirrors the room clock,
 * shows the connected-room status + ring motif, offers one-tap "Join now", and a
 * flow grid of secondary affordances:
 *
 *   - Join with ID — type a meeting name and start it under the room's 8x8 tenant
 *     (`<domain>/<tenant>/<name>`), the new-Spot twin of legacy Spot's "meetNow".
 *   - Dial a number — start the room's meeting and pull a phone into it (the TV fires
 *     the Jitsi iframe `invite` on join → jigasi places the PSTN call). Needs the
 *     meeting JWT's outbound-call feature, so it connects on real 8x8.vc.
 *   - Share content — surface how to share from a laptop (the in-room iPad never
 *     captures a screen itself).
 *   - Calendar — the room's upcoming agenda ("what's next in this room"), in a modal
 *     that fetches the same roomkeeper feed the TV idle screen shows (see CalendarModal).
 */

import { useState } from 'react';
import { Clock, RingMotif, useNow } from '../ui/Clock.js';
import { ArrowRight, Calendar, Monitor, Phone, Video, X } from '../ui/icons.js';
import { Button, Dot, Logo, Pill } from '../ui/components.js';
import { CalendarModal } from './CalendarModal.js';

/** Enter a meeting name and start it under the room's tenant ("Join with ID"). */
function JoinWithIdModal(props: {
    meetingDomain: string;
    meetingTenant: string;
    onJoin: (name: string) => void;
    onClose: () => void;
}): JSX.Element {
    const [name, setName] = useState<string>('');
    // The address the meeting will open under, so the operator sees where they're going.
    const prefix = props.meetingTenant
        ? `${props.meetingDomain}/${props.meetingTenant}/`
        : `${props.meetingDomain}/`;
    // Jitsi room names are URL path segments — keep it simple + safe.
    const clean = name.trim().replace(/\s+/g, '-').replace(/[^A-Za-z0-9._-]/g, '');

    return (
        <div className = "scrim" onClick = { props.onClose }>
            <div className = "modal" style = {{ maxWidth: '46cqw', textAlign: 'left' }} onClick = { e => e.stopPropagation() } data-testid = "rm-joinid">
                <div className = "row" style = {{ justifyContent: 'space-between', marginBottom: '1.2cqw' }}>
                    <span className = "modal__title" style = {{ margin: 0 }}>Join with ID</span>
                    <span className = "linkbtn" onClick = { props.onClose }><X /></span>
                </div>
                <p className = "modal__body" style = {{ margin: '0 0 1cqw' }}>Type a meeting name to start it on the room TV.</p>
                <div className = "joinid__addr" data-testid = "rm-joinid-prefix" style = {{
                    fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: '1.2cqw',
                    opacity: 0.7, marginBottom: '0.4cqw', wordBreak: 'break-all'
                }}>{ prefix }<b style = {{ opacity: clean ? 1 : 0.4 }}>{ clean || 'meeting-name' }</b></div>
                <input
                    className = "text-input"
                    value = { name }
                    placeholder = "Meeting name"
                    autoFocus = { true }
                    onChange = { e => setName(e.target.value) }
                    onKeyDown = { e => { if (e.key === 'Enter' && clean) { props.onJoin(clean); props.onClose(); } } }
                    data-testid = "rm-joinid-input" />
                <div className = "modal__actions" style = {{ marginTop: '1.6cqw' }}>
                    <button
                        className = "btn"
                        disabled = { !clean }
                        onClick = { () => { props.onJoin(clean); props.onClose(); } }
                        data-testid = "rm-joinid-go">
                        <Video /> Start meeting
                    </button>
                </div>
            </div>
        </div>
    );
}

/** Dial a phone number into the room's meeting (E.164, e.g. +14155550123). */
function DialModal(props: { onDial: (number: string) => void; onClose: () => void }): JSX.Element {
    const [number, setNumber] = useState<string>('+');
    const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '+', '0', '⌫'];
    // E.164: a leading + and 8–15 digits. Good enough to gate the Call button.
    const valid = /^\+\d{8,15}$/.test(number);

    const press = (k: string): void => {
        if (k === '⌫') {
            setNumber(prev => prev.slice(0, -1) || '+');
        } else {
            setNumber(prev => (prev + k).slice(0, 16));
        }
    };

    return (
        <div className = "scrim" onClick = { props.onClose }>
            <div className = "modal" style = {{ maxWidth: '34cqw' }} onClick = { e => e.stopPropagation() } data-testid = "rm-dial">
                <div className = "row" style = {{ justifyContent: 'space-between', marginBottom: '0.6cqw' }}>
                    <span className = "modal__title" style = {{ margin: 0 }}>Dial a number</span>
                    <span className = "linkbtn" onClick = { props.onClose }><X /></span>
                </div>
                <p className = "modal__body" style = {{ margin: '0 0 1cqw' }}>Call a phone into the room — include the country code.</p>
                <div className = "dialpad__readout" data-testid = "rm-dial-readout">{ number }</div>
                <div className = "dialpad">
                    { keys.map(k => (
                        <button key = { k } className = "dialkey" onClick = { () => press(k) } data-testid = { `rm-dialkey-${k}` }>{ k }</button>
                    )) }
                </div>
                <div className = "modal__actions" style = {{ marginTop: '1.2cqw' }}>
                    <button
                        className = "btn"
                        disabled = { !valid }
                        onClick = { () => { props.onDial(number); props.onClose(); } }
                        data-testid = "rm-dial-call">
                        <Phone /> Call into the room
                    </button>
                </div>
            </div>
        </div>
    );
}

export function RemoteHome(props: {
    roomName: string;
    /** The paired room's id + access token — used by the Calendar modal's agenda fetch. */
    roomId: string;
    accessToken: string;
    tvConnected: boolean;
    isModerator: boolean;
    meetingDomain: string;
    meetingTenant: string;
    /** Whether this device can capture a screen (desktop) vs the in-room iPad (hint only). */
    canShareHere: boolean;
    onJoin: () => void;
    onJoinWithId: (name: string) => void;
    onDial: (number: string) => void;
    onShare: () => void;
}): JSX.Element {
    const now = useNow();
    const [joinId, setJoinId] = useState<boolean>(false);
    const [dial, setDial] = useState<boolean>(false);
    const [cal, setCal] = useState<boolean>(false);

    return (
        <div className = "rm-home" data-testid = "rm-home">
            <div className = "rm-home__left">
                <RingMotif className = "rm-home__rings" />
                <div className = "row" style = {{ marginBottom: '2cqw' }}>
                    <Logo />
                    <span className = "eyebrow">Remote</span>
                </div>
                <div className = "rm-home__status" data-testid = "rm-connected">
                    <Dot kind = { props.tvConnected ? 'live' : 'idle' } />
                    { props.tvConnected ? `Connected to ${props.roomName}` : 'Reconnecting to the room…' }
                </div>
                <Clock now = { now } room = { props.roomName } small = { true } />
            </div>

            <div className = "rm-home__right">
                <div className = "soon-card">
                    <div className = "soon-card__head">
                        <span className = "eyebrow">Ready when you are</span>
                        { props.isModerator ? <Pill label = "Moderator" variant = "host" /> : null }
                    </div>
                    <div className = "soon-card__title">{ props.roomName }</div>
                    <div className = "soon-card__meta">Starts instantly on the room TV</div>
                    <Button
                        label = "Join now"
                        icon = { <Video /> }
                        block = { true }
                        large = { true }
                        onClick = { props.onJoin }
                        testId = "rm-start-meeting" />
                </div>

                <div className = "flowgrid">
                    <button className = "flowcard flowcard--cal" onClick = { () => setCal(true) } data-testid = "rm-flow-calendar">
                        <Calendar /> Calendar
                    </button>
                    <button className = "flowcard" onClick = { () => setJoinId(true) } data-testid = "rm-flow-joinid">
                        <ArrowRight /> Join with ID
                    </button>
                    <button className = "flowcard flowcard--dial" onClick = { () => setDial(true) } data-testid = "rm-flow-dial">
                        <Phone /> Dial a number
                    </button>
                    <button className = "flowcard flowcard--share" onClick = { props.onShare } data-testid = "rm-flow-share">
                        <Monitor /> Share content
                    </button>
                </div>
            </div>

            { joinId ? (
                <JoinWithIdModal
                    meetingDomain = { props.meetingDomain }
                    meetingTenant = { props.meetingTenant }
                    onJoin = { props.onJoinWithId }
                    onClose = { () => setJoinId(false) } />
            ) : null }
            { dial ? (
                <DialModal onDial = { props.onDial } onClose = { () => setDial(false) } />
            ) : null }
            { cal ? (
                <CalendarModal
                    roomName = { props.roomName }
                    roomId = { props.roomId }
                    accessToken = { props.accessToken }
                    onClose = { () => setCal(false) } />
            ) : null }
        </div>
    );
}
